import * as React from 'react';
import { Input as InputPrimitive } from '@base-ui/react/input';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'cn';

const inputVariants = cva(
  'w-full min-w-0 rounded-md border bg-transparent px-2.5 py-1 transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/30 dark:bg-input/30 dark:disabled:bg-input/80',
  {
    variants: {
      size: {
        default: 'h-8 text-base md:text-sm',
        // Design-system lg: 40px / 16px, apps/web.
        lg: 'h-10 px-3 text-base',
      },
      strong: {
        false: 'border-input',
        // 3:1 border for unlabeled fields (toolbar search).
        true: 'border-input-strong',
      },
    },
    defaultVariants: { size: 'default', strong: false },
  },
);

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> &
  VariantProps<typeof inputVariants> & {
    /** Leading icon, rendered inside the field. */
    icon?: React.ReactNode;
  };

function Input({ className, type, size, strong, icon, ...props }: InputProps) {
  const field = (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={cn(inputVariants({ size, strong }), icon && 'pl-9', className)}
      {...props}
    />
  );
  if (!icon) return field;
  return (
    <div className="relative w-full">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground [&_svg:not([class*='size-'])]:size-4"
      >
        {icon}
      </span>
      {field}
    </div>
  );
}

export { Input, inputVariants };
